import { Link, Navigate, useLocation } from "react-router-dom";
import { useVenueAuth } from "../contexts/VenueAuthContext";
import { useOnlineStatus } from "@dwelldrop/shared";
import type { ReactNode } from "react";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional: restrict to specific role */
  requiredRole?: "venue_owner" | "venue_staff" | "property_operator";
  /** Optional: restrict staff to specific sub-roles (owners and property_operators always bypass) */
  allowedStaffRoles?: ("scanner" | "manager")[];
}

export default function ProtectedRoute({
  children,
  requiredRole,
  allowedStaffRoles,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { user, profile, loading, twoFactor, sessionExpired } = useVenueAuth();
  const { isOnline } = useOnlineStatus();

  // Still loading session
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        <p className="text-sm text-gray-500">Loading your session…</p>
      </div>
    );
  }

  if (!user || !profile) {
    if (!isOnline && user && !sessionExpired) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              You&apos;re offline
            </h2>
            <p className="text-gray-500 text-sm">
              Reconnect to load this page. Your session is still active.
            </p>
          </div>
        </div>
      );
    }
    return <Navigate to={sessionExpired ? "/login?expired=true" : "/login"} replace />;
  }

  // 2FA pending
  if (twoFactor.pendingVerification) {
    return <Navigate to="/verify-2fa" replace />;
  }


  // Redirect property operators away from the root (venue) pages
  if (
    profile.role === "property_operator" &&
    location.pathname === "/"
  ) {
    return <Navigate to="/property-dashboard" replace />;
  }

  // Scanners have no dashboard access — send them to the deal terminal
  if (
    profile.role === "venue_staff" &&
    profile.staffRole === "scanner" &&
    location.pathname === "/"
  ) {
    return <Navigate to="/scanner" replace />;
  }

  // Role check
  if (requiredRole && profile.role !== requiredRole) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-500 text-sm">
            You don&apos;t have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  // Staff sub-role check (owners and property_operators always bypass)
  if (
    allowedStaffRoles &&
    profile.role === "venue_staff" &&
    (!profile.staffRole || !allowedStaffRoles.includes(profile.staffRole))
  ) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            You don't have permission to access this page.
          </p>
          <Link
            to="/"
            className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
